import React from 'react';
import { motion } from 'framer-motion';
import { Microscope, FileText, ExternalLink, ArrowRight } from 'lucide-react';
export function ResearchSection() {
  return <section className="py-24 max-w-7xl mx-auto px-6 bg-white" data-id="element-341">
      <div className="flex flex-col md:flex-row justify-between items-end mb-12" data-id="element-342">
        <div className="max-w-2xl" data-id="element-343">
          <div className="inline-flex items-center space-x-2 mb-4" data-id="element-344">
            <Microscope className="w-5 h-5 text-teal" data-id="element-345" />
            <span className="text-sm font-bold tracking-wider uppercase text-teal" data-id="element-346">
              EWUCRT
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-navy" data-id="element-347">
            Research Excellence & Innovations
          </h2>
          <p className="text-slate-600 text-lg" data-id="element-348">
            Driving impactful discoveries through the Center for Research and
            Training, establishing EWU as a premier research institution.
          </p>
        </div>
        <button className="hidden md:flex items-center text-sm font-semibold text-navy hover:text-teal transition-colors mt-6 md:mt-0" data-id="element-349">
          View All Publications <ArrowRight className="w-4 h-4 ml-2" data-id="element-350" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-351">
        {/* Featured Breakthrough (Spans 2 cols) */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="lg:col-span-2 bg-white border border-slate-200 shadow-lg rounded-2xl overflow-hidden group relative" data-id="element-352">
          <div className="absolute top-0 left-0 w-32 h-32 bg-teal/5 rounded-br-full" data-id="element-353" />
          <div className="p-8 md:p-10 relative z-10 h-full flex flex-col" data-id="element-354">
            <div className="bg-teal/10 text-teal text-xs font-bold px-3 py-1 rounded-full w-fit mb-6 border border-teal/20" data-id="element-355">
              Latest Breakthrough
            </div>
            <h3 className="text-2xl md:text-3xl font-bold leading-tight mb-4 text-navy group-hover:text-teal transition-colors" data-id="element-356">
              Numerical Simulation of Targeted Pulmonary Drug Delivery in
              Patient-based Human Lung Model Under Physiological Conditions
            </h3>
            <p className="text-slate-600 mb-8 max-w-2xl" data-id="element-357">
              A groundbreaking computational study organized by EWUCRT that
              models aerosol deposition in human lungs, offering new pathways
              for targeted respiratory treatments.
            </p>

            <div className="mt-auto flex items-center justify-between border-t border-slate-200 pt-6" data-id="element-358">
              <div className="flex items-center space-x-4" data-id="element-359">
                <div className="flex -space-x-2" data-id="element-360">
                  <div className="w-8 h-8 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-xs font-bold text-slate-700" data-id="element-361">
                    DR
                  </div>
                  <div className="w-8 h-8 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-xs font-bold text-slate-700" data-id="element-362">
                    MS
                  </div>
                </div>
                <span className="text-sm text-slate-500" data-id="element-363">
                  Published in Q1 Journal
                </span>
              </div>
              <button className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-navy hover:text-white hover:border-navy transition-all" data-id="element-364">
                <ExternalLink className="w-4 h-4" data-id="element-365" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Call for Research & Shortcuts */}
        <div className="flex flex-col gap-6" data-id="element-366">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-white border border-slate-200 shadow-md rounded-2xl p-6 flex-grow" data-id="element-367">
            <h4 className="text-lg font-bold mb-4 flex items-center text-slate-900" data-id="element-368">
              <FileText className="w-5 h-5 mr-2 text-gold" data-id="element-369" />
              Call for Research
            </h4>
            <div className="space-y-4" data-id="element-370">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 hover:border-navy transition-colors cursor-pointer" data-id="element-371">
                <div className="text-xs text-gold mb-1 font-medium" data-id="element-372">
                  Deadline: June 30, 2026
                </div>
                <div className="font-semibold text-sm text-slate-800" data-id="element-373">
                  East West Journal of Humanities (Vol. 13)
                </div>
              </div>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 hover:border-navy transition-colors cursor-pointer" data-id="element-374">
                <div className="text-xs text-gold mb-1 font-medium" data-id="element-375">
                  Open Now
                </div>
                <div className="font-semibold text-sm text-slate-800" data-id="element-376">
                  Round 19 Research Proposals
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} data-id="element-377">
            <button className="w-full bg-white border border-slate-200 shadow-md hover:shadow-lg rounded-2xl p-6 flex items-center justify-between group transition-all" data-id="element-378">
              <div className="text-left" data-id="element-379">
                <div className="font-bold text-lg mb-1 text-slate-900" data-id="element-380">
                  Institutional Repository
                </div>
                <div className="text-xs text-slate-500" data-id="element-381">
                  Download peer-reviewed abstracts
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 group-hover:bg-navy group-hover:text-white transition-colors" data-id="element-382">
                <DownloadIcon className="w-4 h-4" data-id="element-383" />
              </div>
            </button>
          </motion.div>
        </div>
      </div>
    </section>;
}
function DownloadIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-384">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-385" />
      <polyline points="7 10 12 15 17 10" data-id="element-386" />
      <line x1="12" x2="12" y1="15" y2="3" data-id="element-387" />
    </svg>;
}